import { Injectable} from '@angular/core';
import { Account} from '@mitel/cloudlink-sdk';
import { Observable, Subject, Subscription} from 'rxjs';
import { AppUserClaimsService } from './app-user-claims.service';
import { AdminService} from './admin.service';
import { UserRoles } from '../constants/constants';
import { tap, shareReplay } from 'rxjs/operators';
import { AppStateService } from './app-state.service';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root'
})
export class AccountsService {

	private currentAccount: Account;
	private currentAccountTags;
	private isPrimeAccountAccountAdminObservable: Observable<any>;
	public isProcessing = false;

	public accountsChanged = new Subject<Account[]>();
	public currentAccountChanged = new Subject<Account>();
	public accountTagChanged = new Subject<any>();
	subscription: Subscription = new Subscription();

	private _accounts: Account[] = [];
	primeAccount: string;
	private _partnerId: string;
	platform: any;

	set accounts(value: Account[]) {
		this._accounts = value;
		this.accountsChanged.next([...this._accounts]);
	}

	get accounts(): Account[] {
		return this._accounts;
	}

	constructor(
		private claimsService: AppUserClaimsService,
		private adminService: AdminService,
		private appStateService: AppStateService
	) {}

	setAccounts(accounts: Account[]) {
		this.accounts = accounts;
	}

	getAccounts() {
			this.adminService.getAllAccounts().then(accounts => {
				this.accounts = accounts;
			});
	}

	getAccountsWithTags(){
		let odata: any = {
			$Top: 100
		}
		this.adminService.getAllAccountsWithTags(odata).then(accounts => {
			this.accounts = accounts;
		});
	}

	getPrimeAccountId() {
		return this.primeAccount;
	}

	addAccounts(accounts: Account[]): void {
		this.accounts = [...this.accounts ? this.accounts : [], ...accounts];
		console.log('Function: addAccounts, this.accounts: ', this.accounts);
	}

	setCurrentAccount(account: Account): void {
		this.currentAccount = account;
		this.currentAccountChanged.next(account);
	}

	getCurrentAccount(): Account {
		return this.currentAccount || undefined;
	}

	getPrimeAccountInformation(tries: number = 2) {
		if ( tries >= 0) {
			this.isPrimeAccountAccountAdminObservable = undefined;
			this.isPrimeAccountAccountAdminObservable = this.getAccountAdminPrimeAccountObservable(); 
				this.subscription.add(this.isPrimeAccountAccountAdminObservable.subscribe((result) => {
					if (result && result.error) {
						console.log('Failed to fetch prime account Id', result.error);
						this.getPrimeAccountInformation(--tries);
					} else {
						console.log('Fetched prime account information: ', result);
					}
				}));
		} 
	}

	// We are using the method that creates the observable for account admin to fetch partner information to do the same when partner admin logs in.
	// after the implementation of the route guards.Now there may not be a need to create an observable to return partner information in case when account admin login.
	// Therefore method name hasn't changed to generic name yet.
	getAccountAdminPrimeAccountObservable() {
		return Observable.create( async (observer) => {
			const claims = this.claimsService.getClaims() as any;
			if (claims && (claims.role === UserRoles.ACCOUNT_ADMIN || claims.role === UserRoles.PARTNER_ADMIN)) {
				try {
					const partner = await this.adminService.getPartner(claims.partnerId)
					this.primeAccount = partner.primeAccount;
					if( claims.role === UserRoles.ACCOUNT_ADMIN ) {
						observer.next(claims.accountId === partner.primeAccount);
					} else {
						observer.next(false);		// This situation is when partner admin is logged in and observable is for accountAdmin
					}
				} catch(error) {
					console.log('Failed to get PrimeAccount');
					observer.next({error});
				}
			} else {
				observer.next(false);
			}
		}).
		pipe(
			tap(() => { console.log('getAccountAdminPrimeAccount() method executed')}),
			shareReplay()
		);
	}

	async isPartnerPrimeAccount(): Promise<boolean>
	{	
		if (this.claimsService.isAssumedRole()) {
			if(this.appStateService.getSupportPortalMode()){
				return this.adminService.getPartner(this.currentAccount.partnerId).then(partner => {
					this.primeAccount = partner.primeAccount;
					return this.currentAccount.accountId === this.primeAccount;
				}).catch(err => {
					this.primeAccount = undefined;
					console.error('cannot set primeAccount', err);
					return false;
				});
			}
			else if ( this.currentAccount && this.primeAccount) {
				return Promise.resolve(this.currentAccount.accountId === this.primeAccount);
			} else if (this.currentAccount ){	// Prime account information is not there, so let's fetch it.
				const claims = this.claimsService.getClaims() as any;
				return this.adminService.getPartner(claims.partnerId).then(partner => {
					this.primeAccount = partner.primeAccount;
					return this.currentAccount.accountId === this.primeAccount;
				}).catch(err => {
					console.error('cannot set primeAccount', err);
					return false;
				});
			} else {
				return Promise.resolve(false);
			}	
		} else {
			return new Promise<boolean>( (resolve, reject) => { 
				this.subscription.add(this.isPrimeAccountAccountAdminObservable.subscribe((result) => {
					if (result.error) {
						resolve(false)
					} else {
						resolve(result);
					}
				}));
			})
		}
	}

	setPlatform(platform) {
		this.platform = platform;
	}

	getPlatform() {
		return this.platform;
	}

	async setCurrentAccountById(accountId: string) {
		console.log('Function: setCurrentAccountById, accountId: ', accountId);
		let index = this.accounts.findIndex(acc => acc.accountId === accountId);
		// if (index < 0) {
			// const accounts = await this.adminService.getAllAccounts();
			// if (accounts && accounts.length > 0) {
			// 	this.setAccounts(accounts);
			// 	let currAcc = accounts.find(acc => acc.accountId === accountId);
			// 	if (currAcc) {
			// 		this.setCurrentAccount(currAcc);
			// 	}
			// }
		if (index < 0) {
			const [account, tags] = await Promise.all([
				this.adminService.getAccount(accountId),
				this.adminService.getAccountTags(accountId)
			]);
			if (account) {
				this.accounts = [...this.accounts, account];
				this.setCurrentAccount(account);
			}
			this.accountTagChanged.next(tags);
			
		} else {
			let account = this.accounts[index];
			console.log('Function: setCurrentAccountById, FOUND account: ', account);
			this.setCurrentAccount(account);
		}
	}

	async getAccountById(accountId: string): Promise<Account> {
		let elIndex = this.accounts.findIndex((account) => account.accountId === accountId);
		if (elIndex !== -1) {
			return Promise.resolve(this.accounts[elIndex]);
		} else {
			let account = await this.adminService.getAccount(accountId);
			this.accounts.push(account);
			return Promise.resolve(account);
		}
	}
	
	
	getAccountTagsById(accountId: string): Promise<any> {
		return this.adminService.getAccountTagsNoFrills(accountId)
			.then(tags => {
				return tags;
			})
			.catch( err => {
				if (err.statusCode !== 404) { 
					console.error('Function: , getAccountTagsById: accountId', accountId, err);
					throw(err);
				}
				else if (err.statusCode == 404 && err.body && err.body.name == "AccountNotFound")
				{
					console.error('Function: , getAccountTagsById: no account accountId', accountId, err);
					throw(err);
				}
				// if we get here, 404 means no account tags
				this.setAccountTag({});
				return {};
			});
	}		

	async deleteAccountTagById(accountId: string, tagId: string): Promise<any> {
		return this.adminService.deleteAccountTag(accountId, tagId)
			.then(response => {
				// tags changed, so update
				return this.getAccountTagsById(accountId)
					.then(tags => this.setAccountTag(tags))
					.then(() => response);
			})
			.catch( err => {
				if (err.statusCode !== 404) {
					console.error('Function: , deleteAccountTagsById: accountId', err);
				}
				return Promise.resolve(err);
			});
	}

	setAccountTag(tags) {
		this.currentAccountTags = tags
		this.accountTagChanged.next(tags);
	}

	getCurrentAccountTags() {
		return this.currentAccountTags || undefined;
	}

	setAccountStatusInModel(disabledAccountId: string, status: boolean = true) {
		let index = this.accounts.findIndex(account => account.accountId === disabledAccountId);
		if (index > -1) {
			this.accounts[index].active = status;
			this.currentAccountChanged.next(this.accounts[index]);
		}
	}

	removeDisabledAccountFromModel(disabledAccountId: any) {
		this.accounts = this.accounts.filter((acc) => acc.accountId !== disabledAccountId);
	}

	mergeAccountWithModel(updatedAccount: Account) {
		let index = this.accounts.findIndex((account) => account.accountId === updatedAccount.accountId);
		if (index >= 0) {
			this.accounts[index] = updatedAccount;
			if ( this.currentAccount != undefined && (updatedAccount.accountId == this.currentAccount.accountId) )
			{
				this.currentAccount = updatedAccount; // we will not fire an event because there might be side effects
													  // maybe with more time and better testing we can determine 
													  // it might be OK to fire an event. This fix has no side effects.
			}
		}
	}

	delegateAuth() {

	}

	destroySubscriptions() {
		if( this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	doesAccountHaveSpecifiedProduct( accountId: string, product: string ): Promise<boolean>
	{
		return this.getAccountTagsById(accountId).then((tags: any) => {
			console.log("The TAGS:", tags);
			if ( tags && tags.products && tags.products.indexOf(product) != -1 ) 
				return true;
			else
				return false;
		});
	}
	
	// to prevent multiple calls to the method above with multiple calls to get the account tags
	doesAccountHaveAnyOneSpecifiedProduct( accountId: string, products: string[] ): Promise<boolean>
	{
		return this.getAccountTagsById(accountId).then((tags: any) => {
			console.log("The TAGS:", tags);
			if ( tags && tags.products )
			{
				for (var i = 0; i < products.length; i++ )
				{
					if ( tags.products.indexOf(products[i]) != -1 ) 
						return true;
				}
			}
			return false; // if we get here it was not found
		});
	}

	isSSOEnforcedForTheCurrentAccount(): boolean
	{
		let ssoEnforced = false;
		if ( this.currentAccount && this.currentAccount.policy && 
			this.currentAccount.policy.disableNativeUserLogin && 
			this.currentAccount.policy.disableNativeUserLogin === true )
			ssoEnforced = true;
		return ssoEnforced;
	}

	isSSOEnforcedForSpecifiedAccount( accountId: string ): Promise<boolean>
	{
		let ssoEnforced = false;
		return this.getAccountById( accountId ).then( account => {

			if ( account && account.policy && 
				account.policy.disableNativeUserLogin && 
				account.policy.disableNativeUserLogin === true )
				ssoEnforced = true;
			return ssoEnforced;
		});
	}

	async getPartnerId(): Promise<string> {
		if (!this._partnerId) {
			let claims = this.claimsService.getClaims() as any;
			if (claims.partnerId) {
				this._partnerId = claims.partnerId;
			} else if (claims.providerAccountId || claims.accountId) {
				await this.adminService.getAccountWithoutFrills(claims.providerAccountId || claims.accountId).then(account => {
					this._partnerId = account.partnerId;
				});
			}
		}
		return this._partnerId;
	}

	async isProximusAccount(): Promise<boolean> {
		let proximusPartnerId = environment.proximusPartnerId;
		let partnerId = await this.getPartnerId();
		return partnerId == proximusPartnerId;
	}
}
